import { useState, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE_OPTIONS = [
  // Institutional / B2B (REITs, asset managers, court receivers)
  { key: "auto", group: "Institutional", label: "Platform Recommends", colors: ["#00FFFF", "#CE93D8", "#4CAF50", "#FFC107"], desc: "Let the platform analyze the room and recommend the best palette" },
  { key: "modern_neutral", group: "Institutional", label: "Modern Neutral", colors: ["#B5A898", "#FFFFFF", "#C4A77D", "#2C2C2C"], desc: "Agreeable Gray walls, white trim, natural oak floor, matte black hardware" },
  { key: "warm_contemporary", group: "Institutional", label: "Warm Contemporary", colors: ["#C5B9A8", "#F0EDE8", "#D4A76A", "#B08D57"], desc: "Accessible Beige walls, alabaster trim, honey oak floor, champagne bronze hardware" },
  { key: "cool_modern", group: "Institutional", label: "Cool Modern", colors: ["#A8A8A8", "#FFFFFF", "#8C8C8C", "#C0C0C0"], desc: "Repose Gray walls, pure white trim, gray oak floor, chrome hardware" },
  { key: "classic_clean", group: "Institutional", label: "Classic Clean", colors: ["#9E9E9E", "#E8E0D4", "#8B7355", "#A0A0A0"], desc: "Mindful Gray walls, Dover White trim, brown oak floor, satin nickel hardware" },
  // 2026 Boutique — Grounded Luxury (cycle-aligned defaults) — 9 palettes for full design range
  { key: "ethereal_meadow", group: "Boutique 2026", label: "Ethereal Meadow", colors: ["#B8D4B8", "#FFF8F0", "#F5E6F0", "#E8D5B7"], desc: "Misty sage, soft cream, whisper pink — bright, hopeful, optimistic. 48-hr Flash Refresh default." },
  { key: "deep_harbor", group: "Boutique 2026", label: "Deep Harbor", colors: ["#1F3A5F", "#FFFFFF", "#C9A878", "#9C7B5A"], desc: "Deep navy, crisp white, brass accents, driftwood LVP — coastal boutique hotel. 14-day Standard Soul default." },
  { key: "earth_hearth", group: "Boutique 2026", label: "Earth Hearth", colors: ["#B5612D", "#F0E6D2", "#5C4033", "#2C2C2C"], desc: "Terracotta, warm cream, walnut wood, matte black — modern farmhouse boutique. 14-day Standard Soul default." },
  { key: "desert_twilight", group: "Boutique 2026", label: "Desert Twilight", colors: ["#5C6E5C", "#3D3D3D", "#D4B896", "#8B6F47"], desc: "Pewter green, charcoal, sand beige, bronze — bold dignified boutique loft. 21-day Full Dignity default." },
  { key: "cathedral_mauve", group: "Boutique 2026", label: "Cathedral Mauve", colors: ["#A88AA0", "#F5EBE8", "#8B7D8A", "#D4AF7B"], desc: "Dusty rose, soft pearl, smoky violet, antique brass — sophisticated feminine adult spaces, transitional housing." },
  { key: "saffron_stone", group: "Boutique 2026", label: "Saffron Stone", colors: ["#D4A24C", "#E8D9C4", "#7A6855", "#3C3A36"], desc: "Warm saffron, stone beige, weathered bronze, soft charcoal — energy and optimism. Great for kitchens + dining." },
  { key: "midnight_sage", group: "Boutique 2026", label: "Midnight Sage", colors: ["#3D4A3D", "#1A1F1A", "#A8B5A2", "#C9A878"], desc: "Deep forest sage, near-black, sage mist, soft brass — luxury feature wall energy. Adult primary + boutique baths." },
  { key: "coastal_linen", group: "Boutique 2026", label: "Coastal Linen", colors: ["#A8C8DC", "#FFFFFF", "#D8C8A8", "#8FAEBD"], desc: "Pale sky blue, crisp white, driftwood, weathered blue — fresh airy hopeful. Good for nurseries, DV transitional." },
  { key: "heirloom_pearl", group: "Boutique 2026", label: "Heirloom Pearl", colors: ["#E8DFD0", "#C8B89C", "#8B7D6B", "#DBA94A"], desc: "Warm cream, soft taupe, deep stone, antique gold — timeless elegance. Senior + multi-generational + dignified turnover." },
  // Family / Kid / Dignified — every unit deserves intention
  { key: "fairy_garden", group: "Family", label: "Fairy Garden", colors: ["#C9B6E0", "#F5E6F0", "#B8D4B8", "#E8C547"], desc: "Soft lavender accent + alabaster, fairy lights, sage & blush accents — magical for young girls" },
  { key: "modern_princess", group: "Family", label: "Modern Princess", colors: ["#F4C2C2", "#FFF4E6", "#E5B5A3", "#FFFFFF"], desc: "Blush + cream, rose gold hardware — elegant feminine without being cartoonish" },
  { key: "adventure_cove", group: "Family", label: "Adventure Cove", colors: ["#7FB3B3", "#E07856", "#E8D5B7", "#FFFFFF"], desc: "Soft teal + coral pop, warm sand textiles — gender-neutral for boys, girls, or shared rooms" },
  { key: "dreamy_pastels", group: "Family", label: "Dreamy Pastels", colors: ["#B8E0D2", "#FFD8B1", "#D8B4E0", "#FFF4E6"], desc: "Mint + peach + lilac — calming for nurseries, toddler rooms, and transitional housing" },
];

// Cycle → default palette mapping (free-range: user can override)
const CYCLE_OPTIONS = [
  { key: "", label: "— None (use any palette) —", palette: "" },
  { key: "swift_48", label: "48-Hour Swift Turn (Flash Refresh)", palette: "ethereal_meadow" },
  { key: "standard_14", label: "14-Day Standard Stabilization (Standard Soul)", palette: "deep_harbor" },
  { key: "heavy_21", label: "21-Day Heavy Recovery (Full Dignity)", palette: "desert_twilight" },
];

const ROOM_TYPES = [
  { key: "living_room", label: "Living Room" },
  { key: "bedroom", label: "Bedroom" },
  { key: "kitchen", label: "Kitchen" },
  { key: "bathroom", label: "Bathroom" },
  { key: "dining_area", label: "Dining Area" },
  { key: "hallway", label: "Hallway / Entry" },
];

const AGE_OPTIONS = [
  { key: "", label: "— Not specified —" },
  { key: "toddler", label: "Toddler (0–3)" },
  { key: "young_child", label: "Young Child (4–8)" },
  { key: "tween", label: "Tween (9–12)" },
  { key: "teen", label: "Teen (13–17)" },
  { key: "adult", label: "Adult" },
  { key: "senior", label: "Senior (60+)" },
  { key: "dv_survivor", label: "DV Survivor / Transitional" },
  { key: "family", label: "Family / Multi-Generational" },
];

const RoomVisualizer = () => {
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [inspirationPreview, setInspirationPreview] = useState(null);
  const [inspirationNote, setInspirationNote] = useState("");
  const [palette, setPalette] = useState("modern_neutral");
  const [roomType, setRoomType] = useState("living_room");
  const [scope, setScope] = useState(["paint_walls", "paint_trim", "flooring_lvp", "fixtures_lighting", "hardware_cabinets"]);
  const [visionPrompt, setVisionPrompt] = useState("");
  const [customPaint, setCustomPaint] = useState("");
  const [ageDemo, setAgeDemo] = useState("");
  const [renterFriendly, setRenterFriendly] = useState(false);
  const [complianceSafe, setComplianceSafe] = useState(true); // Default ON — HUD/HQS/NSPIRE/City Code protected
  // Compliance gates: auto-prompt Vendor Network when these are true
  const [pre1978, setPre1978] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [cycle, setCycle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showSourcing, setShowSourcing] = useState(false);
  const fileRef = useRef(null);
  const inspirationFileRef = useRef(null);

  // Auto-load template if user clicked "Use Template" from the Templates Library
  // Also accept URL query ?template=... so the redirect from /design-studio works
  const loadedTemplateRef = useRef(false);
  if (!loadedTemplateRef.current && typeof window !== "undefined") {
    loadedTemplateRef.current = true;
    try {
      const stash = localStorage.getItem("mf_pending_template");
      if (stash) {
        const t = JSON.parse(stash);
        if (t) {
          if (t.palette) setPalette(t.palette);
          if (t.room_type) setRoomType(t.room_type);
          if (t.cycle) setCycle(t.cycle);
          if (t.age_demographic) setAgeDemo(t.age_demographic);
          if (typeof t.renter_friendly === "boolean") setRenterFriendly(t.renter_friendly);
          if (typeof t.compliance_safe === "boolean") setComplianceSafe(t.compliance_safe);
          if (Array.isArray(t.scope)) setScope(t.scope);
          if (t.vision_prompt) setVisionPrompt(t.vision_prompt);
          localStorage.removeItem("mf_pending_template");
        }
      }
    } catch {}
  }

  // Materials Calculator state — Formula A (roll/sheet quantity)
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcArea, setCalcArea] = useState("");        // total sq ft
  const [calcScrap, setCalcScrap] = useState("0.10");  // scrap factor
  const [calcRollWidth, setCalcRollWidth] = useState("2"); // ft
  const [calcRollLength, setCalcRollLength] = useState("16"); // ft

  const calcRollResult = (() => {
    const a = parseFloat(calcArea), s = parseFloat(calcScrap), w = parseFloat(calcRollWidth), l = parseFloat(calcRollLength);
    if (!a || !w || !l) return null;
    const required = (a * (1 + (isFinite(s) ? s : 0))) / (w * l);
    return Math.ceil(required * 100) / 100;
  })();

  // Wrap-Around Constant — Formula B
  const [wrapDoorWidth, setWrapDoorWidth] = useState(""); // inches
  const wrapConstantC = 2; // inches per side
  const wrapVinylWidth = (() => {
    const d = parseFloat(wrapDoorWidth);
    if (!d) return null;
    return d + 2 * wrapConstantC;
  })();
  const [familyShareOpen, setFamilyShareOpen] = useState(false);
  const [familyShare, setFamilyShare] = useState({ recipient_name: "", recipient_email: "", room_label: "this room", relationship: "Grandma", note: "", sending: false, sent: false, error: null });
  const [autoPitchTarget, setAutoPitchTarget] = useState("institutional");
  const [autoPitchResult, setAutoPitchResult] = useState(null);
  const [autoPitchLoading, setAutoPitchLoading] = useState(false);

  const sendFamilyShare = async () => {
    if (!result || !result.image_base64) return;
    setFamilyShare(prev => ({ ...prev, sending: true, error: null }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/design/family-share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          after_image_b64: result.image_base64,
          recipient_name: familyShare.recipient_name,
          recipient_email: familyShare.recipient_email,
          room_label: familyShare.room_label || "this room",
          palette_name: selectedPalette?.label || "",
          sender_relationship: familyShare.relationship,
          personal_note: familyShare.note,
        })
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || "Send failed");
      }
      setFamilyShare(prev => ({ ...prev, sending: false, sent: true, error: null }));
    } catch (e) {
      setFamilyShare(prev => ({ ...prev, sending: false, error: e.message }));
    }
  };

  const generateAutoPitch = async () => {
    setAutoPitchLoading(true);
    setAutoPitchResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/design/auto-pitch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          palette_key: palette,
          cycle: cycle,
          scope: scope,
          age_demographic: ageDemo,
          renter_friendly: renterFriendly,
          target_audience: autoPitchTarget,
        })
      });
      if (r.ok) {
        const data = await r.json();
        setAutoPitchResult(data);
      }
    } catch {}
    setAutoPitchLoading(false);
  };

  // Build multi-vendor procurement URLs based on palette + selected scope
  // When complianceSafe is ON, search queries are prepended with HUD/HQS/NSPIRE/City Code compliance keywords
  // and each material is tagged with the certifications it must carry
  const buildVendorList = (paletteObj, scopeArr) => {
    if (!paletteObj) return [];
    const desc = (paletteObj.desc || "").toLowerCase();
    const items = [];
    // Compliance keyword injection per category
    const comp = (catKey) => {
      if (!complianceSafe) return "";
      const map = {
        paint: "low-voc greenguard certified ",
        flooring: "class-1 fire-rated low-voc ",
        backsplash: "class-a fire-rated heat-resistant ",
        lighting: "ul-listed energy-star ",
        hardware: "lead-free ada-compliant ",
        counters: "fda food-safe non-porous ",
        bathwrap: "microban antimicrobial mold-resistant ",
        doors: "fire-rated 20-minute ",
        install: "non-toxic low-voc ",
        wallcovering: "class-a fire-rated low-voc peel-and-stick "
      };
      return map[catKey] || "";
    };
    // Compliance tags shown as visual badges per material
    const tagsFor = (catKey) => {
      if (!complianceSafe) return [];
      const map = {
        paint: ["UL GREENGUARD", "LOW-VOC"],
        flooring: ["CLASS 1 FIRE", "LOW-VOC", "HUD HQS"],
        backsplash: ["ASTM E84 CLASS A", "HEAT-RESISTANT"],
        lighting: ["UL-LISTED", "ENERGY STAR"],
        hardware: ["LEAD-FREE", "ADA"],
        counters: ["FDA FOOD-SAFE", "NON-POROUS"],
        bathwrap: ["MICROBAN", "MOLD-RESISTANT", "HUD HQS"],
        doors: ["20-MIN FIRE-RATED", "FORT WORTH CODE"],
        install: ["NON-TOXIC", "LOW-VOC"],
        wallcovering: ["ASTM E84 CLASS A", "LOW-VOC"]
      };
      return map[catKey] || [];
    };
    // Operational doctrine — Prep Action + Scrap Factor + Invisible Failure prevention per material (May 13, 2026)
    // See /app/memory/PREP_ACTIONS_AND_FORMULAS.md for the full doctrine
    const prepFor = (catKey) => {
      const map = {
        paint: { prep: "TSP Wash + 4hr Dry", scrap: 0.10, invisible: "Skipping TSP = paint bubbles in 30 days." },
        flooring: { prep: "Subfloor Self-Level (>3/16\" deviation = failure)", scrap: 0.10, invisible: "No expansion gap = click joints separate in summer humidity. Leave 5/16\" at walls." },
        backsplash: { prep: "Stove Pull + Grease Trap Clean (3\" clearance from heat)", scrap: 0.15, invisible: "Caulk before grout cures = trapped moisture = tile lift. Wait 24hr." },
        lighting: { prep: "Power-Off Verify + Junction Box Check", scrap: 0.05, invisible: "Junction box must be load-rated for fixture weight + Energy Star compliance." },
        hardware: { prep: "Old Hole Fill (if pull center changes) + 24hr cure", scrap: 0.05, invisible: "Visible old holes through new pulls if center-to-center mismatch." },
        counters: { prep: "Surface De-greasing + Dry Cure", scrap: 0.15, invisible: "Adhesive fails on laminate seams >1/16\". Pre-skim with bondo." },
        bathwrap: { prep: "Mold Audit + 10% Bleach Treatment (48hr cure)", scrap: 0.20, invisible: "Wrapping over active mold = HUD failure + tenant lawsuit. Edge wicking = mold returns. Pre-seal perimeter with food-grade silicone." },
        doors: { prep: "Header Stud Locate (2 minimum)", scrap: 0.05, invisible: "Track in drywall only = falls in 2 weeks. Must hit framing." },
        install: { prep: "Surface Wipe + Dust-Free Verify", scrap: 0.05, invisible: "Command Strips fail on textured/dusty surfaces. Wipe + 1hr dry." },
        wallcovering: { prep: "TSP Wash + Dry + Liner Prime", scrap: 0.10, invisible: "Over-stretched = adhesive crystallizes = bubbles. Let it lay, press from center out." },
        cabinet_wrap: { prep: "Edge De-greasing (Krud Kutter or 70% IPA)", scrap: 0.15, invisible: "2-pass heat gun mandatory: tack at 130°F, set at 180°F. Drill pull holes BEFORE wrap install. Wrap 2\" around back edge for Primer 94 grip." },
        slat_wall: { prep: "Vertical Leveling (4ft level + shim >1/8\")", scrap: 0.05, invisible: "Need 1/8\" expansion gap at ceiling + floor or buckles in summer humidity." }
      };
      return map[catKey] || null;
    };

    if (scopeArr.includes("paint_walls")) {
      const wall = desc.match(/(\w[\w\s]*?) walls/i);
      items.push({ category: "Paint", catKey: "paint", label: `${comp("paint")}Wall paint: ${customPaint || (wall ? wall[1] : paletteObj.label)}`, compliance: tagsFor("paint") });
    }
    if (scopeArr.includes("paint_trim")) {
      const trim = desc.match(/([\w\s]+?) trim/i);
      items.push({ category: "Paint", catKey: "paint", label: `${comp("paint")}Trim paint: ${trim ? trim[1].trim() : "white semi-gloss"}`, compliance: tagsFor("paint") });
    }
    if (scopeArr.includes("flooring_lvp")) {
      const floor = desc.match(/([\w\s]+?) floor/i);
      items.push({ category: "Flooring", catKey: "flooring", label: `${comp("flooring")}LVP flooring: ${floor ? floor[1].trim() : paletteObj.label}`, compliance: tagsFor("flooring") });
    }
    if (scopeArr.includes("fixtures_lighting")) {
      items.push({ category: "Lighting", catKey: "lighting", label: `${comp("lighting")}${paletteObj.label} lighting fixtures${renterFriendly ? " (plug-in or battery)" : ""}`, compliance: tagsFor("lighting") });
    }
    if (scopeArr.includes("hardware_doors")) {
      items.push({ category: "Hardware", catKey: "hardware", label: `${comp("hardware")}Door hardware: ${paletteObj.label}`, compliance: tagsFor("hardware") });
    }
    if (scopeArr.includes("countertops")) {
      items.push({ category: "Counters", catKey: "counters", label: `${comp("counters")}${renterFriendly ? "Peel-and-stick" : "Quartz"} countertop overlay`, compliance: tagsFor("counters") });
    }
    if (scopeArr.includes("fixtures_bath")) {
      items.push({ category: "Bath Wrap", catKey: "bathwrap", label: `${comp("bathwrap")}Hygienic marble bathroom wrap`, compliance: tagsFor("bathwrap"), autoAdd: ["+2 tubes mildew-resistant food-grade silicone sealant"] });
    }
    if (scopeArr.includes("fixtures_kitchen")) {
      items.push({ category: "Backsplash", catKey: "backsplash", label: `${comp("backsplash")}Heat-shield kitchen backsplash tile`, compliance: tagsFor("backsplash"), autoAdd: ["+1 tube food-grade clear silicone"] });
    }
    if (scopeArr.includes("hardware_cabinets")) {
      items.push({ category: "Hardware", catKey: "hardware", label: `${comp("hardware")}Cabinet hardware: ${(desc.match(/([\w\s]+?) hardware/i) || [,paletteObj.label])[1].trim()}`, compliance: tagsFor("hardware"), autoAdd: ["+ Heat gun (if not on-truck) for vinyl wrap install"] });
    }
    if (scopeArr.includes("barn_door")) {
      items.push({ category: "Doors", catKey: "doors", label: `${comp("doors")}Sliding barn door + track hardware kit (no structural reframing)`, compliance: tagsFor("doors") });
    }
    // Renter-friendly extras
    if (renterFriendly && scopeArr.includes("paint_walls")) {
      items.push({ category: "Wallcovering", catKey: "wallcovering", label: `${comp("wallcovering")}Peel-and-stick wallpaper (removable, damage-free)`, compliance: tagsFor("wallcovering") });
    }
    if (renterFriendly) {
      items.push({ category: "Install", catKey: "install", label: `${comp("install")}Command Strips, 3M removable hooks, tension rods (damage-free mounting)`, compliance: tagsFor("install") });
    }

    // Add per-item vendor URLs — now includes Wayfair Pro and Sherwin-Williams Pro
    return items.map(it => {
      const q = encodeURIComponent(it.label);
      const cat = it.category.toLowerCase();
      const isPaint = cat === "paint";
      const isFlooring = cat === "flooring" || cat === "counters";
      const isLighting = cat === "lighting";
      const isHardware = cat === "hardware";
      const isWallcovering = cat === "wallcovering";
      const isDoors = cat === "doors";
      // Attach prep doctrine to each item
      const prep = prepFor(it.catKey);
      const vendors = [
        { name: "Amazon Pro", url: `https://www.amazon.com/s?k=${q}&i=industrial`, priority: 4 },
        { name: "Lowe's Pro", url: `https://www.lowes.com/pl/pro?searchTerm=${q}`, priority: 2 },
        { name: "Home Depot Pro", url: `https://www.homedepot.com/s/${q}?NCNI-5`, priority: 2 },
        { name: "Sherwin-Williams Pro", url: `https://www.sherwin-williams.com/painting-contractors/products/search?searchTerm=${q}`, priority: isPaint ? 1 : 6 },
        { name: "Floor & Decor Pro", url: `https://www.flooranddecor.com/search?q=${q}`, priority: isFlooring ? 1 : 6 },
        { name: "Build with Ferguson", url: `https://www.build.com/search?term=${q}`, priority: isLighting ? 1 : 5 },
        { name: "Cabinets To Go", url: `https://www.cabinetstogo.com/search?q=${q}`, priority: isHardware ? 1 : 6 },
        { name: "Wayfair Pro", url: `https://www.wayfair.com/keyword.php?keyword=${q}`, priority: (isWallcovering || isLighting || isDoors) ? 1 : 3 },
        { name: "Temu", url: `https://www.temu.com/search_result.html?search_key=${q}`, priority: renterFriendly ? 2 : 5 }
      ].sort((a, b) => a.priority - b.priority).slice(0, 5); // top 5 vendors
      return { ...it, vendors, prep };
    });
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setResult(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleInspiration = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setInspirationPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const toggleScope = (key) => {
    setScope(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const visualize = async () => {
    if (!photo) return;
    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.readAsDataURL(photo);
      });

      const r = await fetch(`${BACKEND_URL}/api/visualize-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          photo_base64: base64,
          palette,
          room_type: roomType.replace(/_/g, " "),
          scope,
          vision_prompt: visionPrompt,
          inspiration_note: inspirationNote,
          custom_paint: customPaint,
          age_demographic: ageDemo,
          renter_friendly: renterFriendly,
          cycle: cycle,
        })
      });

      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || "Visualization failed");
      }

      const data = await r.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    }
    setGenerating(false);
  };

  const selectedPalette = PALETTE_OPTIONS.find(p => p.key === palette);
  const institutionalPalettes = PALETTE_OPTIONS.filter(p => p.group === "Institutional");
  const boutiquePalettes = PALETTE_OPTIONS.filter(p => p.group === "Boutique 2026");
  const familyPalettes = PALETTE_OPTIONS.filter(p => p.group === "Family");

  // Cycle selection auto-applies the default palette (user can still override)
  const handleCycleChange = (cycleKey) => {
    setCycle(cycleKey);
    const c = CYCLE_OPTIONS.find(x => x.key === cycleKey);
    if (c && c.palette) setPalette(c.palette);
  };

  return (
    <div className="viz-section" data-testid="room-visualizer">
      <h2>Design Studio — Room Visualizer</h2>
      <p className="ops-lead">
        <strong style={{ color: "#00FFFF" }}>Every unit deserves dignity.</strong> 18 curated palettes (5 institutional B2B · 9 Boutique 2026 grounded luxury · 4 family/dignified) plus full-spectrum custom paint access. Compliance-Safe mode protects you on all four sides — HUD, code enforcement, coalitions, insurance.
      </p>

      <div className="viz-form">
        {/* Photo Upload — BEFORE */}
        <div className="viz-upload-area" onClick={() => fileRef.current?.click()} data-testid="viz-upload">
          {photoPreview ? (
            <div className="viz-photo-preview">
              <img src={photoPreview} alt="Room before" className="viz-before-img" />
              <span className="viz-photo-label">BEFORE — Tap to change photo</span>
            </div>
          ) : (
            <div className="viz-upload-placeholder">
              <i className="fas fa-camera"></i>
              <span>Take a photo or upload a room image (BEFORE)</span>
              <span className="viz-upload-hint">Works best with good lighting and a wide angle</span>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} data-testid="viz-file-input" />
        </div>

        {/* Inspiration Image Upload — optional */}
        <div className="viz-field">
          <label>
            <i className="fas fa-lightbulb" style={{ marginRight: 8, color: "#FFC107" }}></i>
            Inspiration Image (Optional) <span style={{ color: "#7E92A8", fontWeight: 400, fontSize: 12 }}>— upload a bed, paint chip, or style reference</span>
          </label>
          <div className="viz-inspiration-row" style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
            <div
              onClick={() => inspirationFileRef.current?.click()}
              data-testid="viz-inspiration-upload"
              style={{
                flex: "0 0 160px",
                minHeight: 120,
                border: "1.5px dashed rgba(0,255,255,0.3)",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background: "rgba(0,255,255,0.04)",
                overflow: "hidden"
              }}
            >
              {inspirationPreview ? (
                <img src={inspirationPreview} alt="Inspiration" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ color: "#7E92A8", fontSize: 12, textAlign: "center", padding: 8 }}>
                  <i className="fas fa-plus" style={{ fontSize: 18, marginBottom: 6, display: "block", color: "#00FFFF" }}></i>
                  Add inspiration<br />image
                </div>
              )}
              <input ref={inspirationFileRef} type="file" accept="image/*" onChange={handleInspiration} style={{ display: "none" }} data-testid="viz-inspiration-input" />
            </div>
            <input
              type="text"
              value={inspirationNote}
              onChange={(e) => setInspirationNote(e.target.value)}
              placeholder="Describe the inspiration: e.g. 'White canopy twin bed from mom' or 'SW Romance pink swatch'"
              data-testid="viz-inspiration-note"
              style={{
                flex: 1,
                minWidth: 220,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "#fff",
                fontSize: 14
              }}
            />
          </div>
        </div>

        {/* Custom Vision Prompt */}
        <div className="viz-field">
          <label>
            <i className="fas fa-wand-magic-sparkles" style={{ marginRight: 8, color: "#CE93D8" }}></i>
            Your Vision (the more you describe, the better)
          </label>
          <textarea
            value={visionPrompt}
            onChange={(e) => setVisionPrompt(e.target.value)}
            placeholder="Example: Bedroom for my 4-year-old granddaughter Cheyenne. Soft lavender accent wall, white twin bed with canopy frame, fairy lights along ceiling, butterfly wall decals, cozy reading nook with a pink bean bag chair, low white bookshelf, warm rug."
            rows={4}
            data-testid="viz-vision-prompt"
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid rgba(206,147,216,0.3)",
              background: "rgba(206,147,216,0.06)",
              color: "#fff",
              fontSize: 14,
              fontFamily: "inherit",
              resize: "vertical"
            }}
          />
        </div>

        {/* Room Type */}
        <div className="viz-field">
          <label>Room Type</label>
          <div className="viz-room-types">
            {ROOM_TYPES.map(r => (
              <button key={r.key} className={`viz-room-btn ${roomType === r.key ? "active" : ""}`} onClick={() => setRoomType(r.key)} data-testid={`viz-room-${r.key}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Age / Demographic */}
        <div className="viz-field">
          <label>Who is this space for? <span style={{ color: "#7E92A8", fontWeight: 400, fontSize: 12 }}>— guides platform selection on age-appropriate design</span></label>
          <select
            value={ageDemo}
            onChange={(e) => setAgeDemo(e.target.value)}
            data-testid="viz-age-demographic"
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
              color: "#fff",
              fontSize: 14
            }}
          >
            {AGE_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key} style={{ background: "#030B1A" }}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Custom Paint Color / SKU — FULL SPECTRUM access to SW / Behr / Valspar / Glidden catalogs */}
        <div className="viz-field">
          <label style={{ color: "#4CAF50", fontWeight: 700 }}>
            <i className="fas fa-paint-roller" style={{ marginRight: 8, color: "#4CAF50" }}></i>
            🎨 Full Paint Spectrum — Type ANY Color or SKU <span style={{ color: "#7E92A8", fontWeight: 400, fontSize: 12 }}>— overrides palette wall color</span>
          </label>
          <input
            type="text"
            value={customPaint}
            onChange={(e) => setCustomPaint(e.target.value)}
            placeholder="Pull any color: e.g. 'Sherwin-Williams Romance SW 6323' or 'Behr Pink Moment PPU17-10' or 'Valspar Lilac Lust'"
            data-testid="viz-custom-paint"
            style={{
              width: "100%",
              padding: "14px 16px",
              borderRadius: 10,
              border: "2px solid rgba(76,175,80,0.4)",
              background: "rgba(76,175,80,0.08)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500
            }}
          />
          <small style={{ color: "#ADBED1", fontSize: 12, display: "block", marginTop: 8, lineHeight: 1.5 }}>
            Browse full catalogs: <a href="https://www.sherwin-williams.com/homeowners/color/find-and-explore-colors/paint-colors-by-family" target="_blank" rel="noopener noreferrer" style={{ color: "#00FFFF", textDecoration: "underline" }}>Sherwin-Williams</a> · <a href="https://www.homedepot.com/b/Paint-Interior-Paint-Behr/N-5yc1vZc4o6" target="_blank" rel="noopener noreferrer" style={{ color: "#00FFFF", textDecoration: "underline" }}>Behr (Home Depot)</a> · <a href="https://www.lowes.com/c/Interior-paint-Paint-Painting-supplies" target="_blank" rel="noopener noreferrer" style={{ color: "#00FFFF", textDecoration: "underline" }}>Valspar (Lowe's)</a> · <a href="https://www.glidden.com/colors" target="_blank" rel="noopener noreferrer" style={{ color: "#00FFFF", textDecoration: "underline" }}>Glidden</a>. AI will match the exact color you specify.
          </small>
        </div>

        {/* Renter-Friendly Mode Toggle */}
        <div className="viz-field">
          <label
            data-testid="viz-renter-friendly-label"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              borderRadius: 12,
              border: `2px solid ${renterFriendly ? "#00FFFF" : "rgba(255,255,255,0.12)"}`,
              background: renterFriendly ? "rgba(0,255,255,0.08)" : "rgba(255,255,255,0.03)",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onClick={() => setRenterFriendly(!renterFriendly)}
          >
            <input
              type="checkbox"
              checked={renterFriendly}
              onChange={(e) => setRenterFriendly(e.target.checked)}
              data-testid="viz-renter-friendly-toggle"
              style={{ width: 20, height: 20, cursor: "pointer" }}
            />
            <div style={{ flex: 1 }}>
              <strong style={{ color: renterFriendly ? "#00FFFF" : "#fff", display: "block", marginBottom: 4 }}>
                Renter-Friendly / Damage-Free Mode {renterFriendly && "✓"}
              </strong>
              <span style={{ color: "#ADBED1", fontSize: 12 }}>
                AI uses peel-and-stick wallpaper, Command Strips, removable decals, floating LVP, and tension rods — every install reverses with zero damage on tenant move-out. Perfect for Section 8, voucher units, and DV transitional housing.
              </span>
            </div>
          </label>
        </div>

        {/* Compliance-Safe Materials Toggle — Protected on all four sides */}
        <div className="viz-field">
          <label
            data-testid="viz-compliance-safe-label"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              borderRadius: 12,
              border: `2px solid ${complianceSafe ? "#00DC78" : "rgba(255,255,255,0.12)"}`,
              background: complianceSafe ? "rgba(0,220,120,0.08)" : "rgba(255,255,255,0.03)",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onClick={() => setComplianceSafe(!complianceSafe)}
          >
            <input
              type="checkbox"
              checked={complianceSafe}
              onChange={(e) => setComplianceSafe(e.target.checked)}
              data-testid="viz-compliance-safe-toggle"
              style={{ width: 20, height: 20, cursor: "pointer" }}
            />
            <div style={{ flex: 1 }}>
              <strong style={{ color: complianceSafe ? "#00DC78" : "#fff", display: "block", marginBottom: 4 }}>
                <i className="fas fa-shield-halved" style={{ marginRight: 6 }}></i>
                Compliance-Safe Materials Only {complianceSafe && "✓"}
              </strong>
              <span style={{ color: "#ADBED1", fontSize: 12, lineHeight: 1.5 }}>
                Only pulls products certified for: <strong style={{ color: "#fff" }}>ASTM E84 Class A fire-rated</strong> · <strong style={{ color: "#fff" }}>UL Greenguard low-VOC</strong> · <strong style={{ color: "#fff" }}>Microban antimicrobial</strong> · <strong style={{ color: "#fff" }}>FDA food-safe</strong> · <strong style={{ color: "#fff" }}>HUD HQS/NSPIRE</strong> · <strong style={{ color: "#fff" }}>Fort Worth City Code</strong>. Protected on all four sides — housing authorities, code enforcement, coalitions, and insurance.
              </span>
            </div>
          </label>
        </div>

        {/* Compliance Gate — Pre-1978 (lead paint) + Pool (safety inspection) */}
        <div className="viz-field" data-testid="viz-compliance-gate">
          <label style={{ marginBottom: 8 }}>
            <i className="fas fa-triangle-exclamation" style={{ marginRight: 8, color: "#F59E0B" }}></i>
            Compliance Gate — flag out-of-scope work
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label
              style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10,
                border: `2px solid ${pre1978 ? "#EF4444" : "rgba(255,255,255,0.10)"}`,
                background: pre1978 ? "rgba(239,68,68,0.10)" : "rgba(255,255,255,0.03)",
                cursor: "pointer", transition: "all 0.18s",
              }}
              onClick={() => setPre1978(!pre1978)}
              data-testid="viz-pre1978-label"
            >
              <input type="checkbox" checked={pre1978} readOnly data-testid="viz-pre1978-toggle" style={{ marginTop: 3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ color: pre1978 ? "#FCA5A5" : "#fff", display: "block", marginBottom: 3, fontSize: 13 }}>
                  Pre-1978 unit {pre1978 && "⚠"}
                </strong>
                <span style={{ color: "#ADBED1", fontSize: 11, lineHeight: 1.45 }}>
                  Federal RRP Rule + HUD HQS — lead paint test required before any disturbance &gt;6 sq ft.
                </span>
              </div>
            </label>
            <label
              style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10,
                border: `2px solid ${hasPool ? "#0EA5E9" : "rgba(255,255,255,0.10)"}`,
                background: hasPool ? "rgba(14,165,233,0.10)" : "rgba(255,255,255,0.03)",
                cursor: "pointer", transition: "all 0.18s",
              }}
              onClick={() => setHasPool(!hasPool)}
              data-testid="viz-pool-label"
            >
              <input type="checkbox" checked={hasPool} readOnly data-testid="viz-pool-toggle" style={{ marginTop: 3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ color: hasPool ? "#7DD3FC" : "#fff", display: "block", marginBottom: 3, fontSize: 13 }}>
                  Property has pool {hasPool && "⚠"}
                </strong>
                <span style={{ color: "#ADBED1", fontSize: 11, lineHeight: 1.45 }}>
                  Tarrant Co annual inspection + anti-entrapment compliance — pool partner needed.
                </span>
              </div>
            </label>
          </div>
          {(pre1978 || hasPool) && (
            <div data-testid="viz-compliance-prompt" style={{
              marginTop: 12, padding: "14px 16px", borderRadius: 10,
              background: "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(14,165,233,0.10) 100%)",
              border: "1px solid rgba(245,158,11,0.40)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <strong style={{ color: "#fcd34d", fontSize: 13, display: "block", marginBottom: 3 }}>
                    ⚠ Out-of-scope work flagged
                  </strong>
                  <span style={{ color: "#ADBED1", fontSize: 12, lineHeight: 1.5 }}>
                    {pre1978 && <>This pre-1978 unit needs <strong style={{ color: "#FCA5A5" }}>lead paint testing</strong> from a community vendor. </>}
                    {hasPool && <>Pool requires <strong style={{ color: "#7DD3FC" }}>safety + inspection</strong> from a CPO-certified operator. </>}
                    Use a community partner (not a corporate chain) — fair pricing, faster scheduling.
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {pre1978 && (
                    <button
                      type="button"
                      data-testid="viz-find-lead-vendor"
                      onClick={(e) => {
                        e.preventDefault();
                        try { sessionStorage.setItem("mf_vendor_network_prefilter", "lead_paint"); } catch {}
                        window.dispatchEvent(new CustomEvent("mf-switch-tab", { detail: { hub: "run-projects", tab: "vendor-network" } }));
                      }}
                      style={{ background: "rgba(239,68,68,0.20)", color: "#FCA5A5", border: "1px solid #EF4444", padding: "0.5rem 0.9rem", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      🎨 Find Lead-Paint Vendor
                    </button>
                  )}
                  {hasPool && (
                    <button
                      type="button"
                      data-testid="viz-find-pool-vendor"
                      onClick={(e) => {
                        e.preventDefault();
                        try { sessionStorage.setItem("mf_vendor_network_prefilter", "pool_safety"); } catch {}
                        window.dispatchEvent(new CustomEvent("mf-switch-tab", { detail: { hub: "run-projects", tab: "vendor-network" } }));
                      }}
                      style={{ background: "rgba(14,165,233,0.20)", color: "#7DD3FC", border: "1px solid #0EA5E9", padding: "0.5rem 0.9rem", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      🏊 Find Pool Vendor
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>


        {/* Stabilization Cycle — auto-suggests Boutique palette */}
        <div className="viz-field">
          <label>
            <i className="fas fa-clock" style={{ marginRight: 8, color: "#FFC107" }}></i>
            Stabilization Cycle <span style={{ color: "#7E92A8", fontWeight: 400, fontSize: 12 }}>— picks a Boutique 2026 palette default (you can still change it)</span>
          </label>
          <select
            value={cycle}
            onChange={(e) => handleCycleChange(e.target.value)}
            data-testid="viz-cycle"
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid rgba(255,193,7,0.3)",
              background: "rgba(255,193,7,0.06)",
              color: "#fff",
              fontSize: 14
            }}
          >
            {CYCLE_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key} style={{ background: "#030B1A" }}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Palette Selection — grouped */}
        <div className="viz-field">
          <label>Design Palette</label>
          <div style={{ marginBottom: 10, fontSize: 12, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Institutional (B2B)</div>
          <div className="viz-palettes">
            {institutionalPalettes.map(p => (
              <div key={p.key} className={`viz-palette-card ${palette === p.key ? "active" : ""}`} onClick={() => setPalette(p.key)} data-testid={`viz-palette-${p.key}`}>
                <div className="viz-palette-colors">
                  {p.colors.map((c, i) => <div key={i} className="viz-palette-swatch" style={{ background: c }}></div>)}
                </div>
                <strong>{p.label}</strong>
                <span className="viz-palette-desc">{p.desc}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 18, marginBottom: 10, fontSize: 12, color: "#FFC107", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Boutique 2026 (Grounded Luxury · Cycle-Aligned)</div>
          <div className="viz-palettes">
            {boutiquePalettes.map(p => (
              <div key={p.key} className={`viz-palette-card ${palette === p.key ? "active" : ""}`} onClick={() => setPalette(p.key)} data-testid={`viz-palette-${p.key}`}>
                <div className="viz-palette-colors">
                  {p.colors.map((c, i) => <div key={i} className="viz-palette-swatch" style={{ background: c }}></div>)}
                </div>
                <strong>{p.label}</strong>
                <span className="viz-palette-desc">{p.desc}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 18, marginBottom: 10, fontSize: 12, color: "#CE93D8", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Family & Dignified (Tenant-Occupied)</div>
          <div className="viz-palettes">
            {familyPalettes.map(p => (
              <div key={p.key} className={`viz-palette-card ${palette === p.key ? "active" : ""}`} onClick={() => setPalette(p.key)} data-testid={`viz-palette-${p.key}`}>
                <div className="viz-palette-colors">
                  {p.colors.map((c, i) => <div key={i} className="viz-palette-swatch" style={{ background: c }}></div>)}
                </div>
                <strong>{p.label}</strong>
                <span className="viz-palette-desc">{p.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scope */}
        <div className="viz-field">
          <label>What's Being Changed</label>
          <div className="viz-scope-grid">
            {[
              { key: "paint_walls", label: "Walls (Paint)" },
              { key: "paint_trim", label: "Trim & Doors" },
              { key: "flooring_lvp", label: "Flooring (LVP)" },
              { key: "fixtures_lighting", label: "Lighting" },
              { key: "fixtures_kitchen", label: "Kitchen Fixtures" },
              { key: "fixtures_bath", label: "Bath Fixtures" },
              { key: "hardware_cabinets", label: "Cabinet Hardware" },
              { key: "hardware_doors", label: "Door Hardware" },
              { key: "countertops", label: "Countertops" },
              { key: "barn_door", label: "Sliding Barn Door (no reframing)" },
              { key: "deep_clean", label: "Deep Clean" },
            ].map(s => (
              <label key={s.key} className={`viz-scope-item ${scope.includes(s.key) ? "active" : ""}`}>
                <input type="checkbox" checked={scope.includes(s.key)} onChange={() => toggleScope(s.key)} data-testid={`viz-scope-${s.key}`} />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button onClick={visualize} disabled={!photo || generating} className="viz-generate-btn" data-testid="viz-generate-btn">
          {generating ? "Generating Visualization (30-60 seconds)..." : "Visualize This Room"}
        </button>
      </div>

      {error && <div className="viz-error" data-testid="viz-error">{error}</div>}

      {/* Result */}
      {result && (
        <div className="viz-result" data-testid="viz-result">
          <h3>Before & After</h3>
          <p className="viz-result-palette">Palette: <strong>{selectedPalette?.label}</strong> — {selectedPalette?.desc}</p>
          {renterFriendly && (
            <div style={{ background: "rgba(0,255,255,0.08)", border: "1px solid rgba(0,255,255,0.3)", borderRadius: 8, padding: "10px 14px", margin: "10px 0", color: "#00FFFF", fontSize: 13, fontWeight: 600 }}>
              ✓ Renter-Friendly Mode active — all materials sourced are damage-free / fully removable
            </div>
          )}
          <div className="viz-comparison">
            <div className="viz-comparison-panel">
              <span className="viz-comparison-label">BEFORE</span>
              <img src={photoPreview} alt="Before" className="viz-result-img" />
            </div>
            <div className="viz-comparison-panel">
              <span className="viz-comparison-label viz-label-after">AFTER (Platform Visualization)</span>
              <img src={`data:image/png;base64,${result.image_base64}`} alt="After visualization" className="viz-result-img" />
            </div>
          </div>
          <div className="viz-result-actions">
            <a href={`data:image/png;base64,${result.image_base64}`} download={`MF-Visualization-${result.viz_id}.png`} className="viz-download-btn" data-testid="viz-download">Download After Image</a>
            <button onClick={() => setShowSourcing(!showSourcing)} className="viz-source-btn" data-testid="viz-source-materials-btn">
              {showSourcing ? "Hide Vendor Sources" : "Source These Materials (9 Vendors)"}
            </button>
            <button onClick={() => setFamilyShareOpen(!familyShareOpen)} data-testid="viz-family-share-btn" style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #CE93D8", background: "rgba(206,147,216,0.12)", color: "#CE93D8", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {familyShareOpen ? "Close Family Share" : "💜 Share with Family"}
            </button>
            <button onClick={generateAutoPitch} disabled={autoPitchLoading} data-testid="viz-autopitch-btn" style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #00FFFF", background: "rgba(0,255,255,0.10)", color: "#00FFFF", fontSize: 13, fontWeight: 700, cursor: autoPitchLoading ? "wait" : "pointer" }}>
              {autoPitchLoading ? "Generating B2B pitch…" : "🎯 Generate B2B Pitch"}
            </button>
            <button onClick={() => { setResult(null); setPhoto(null); setPhotoPreview(null); setShowSourcing(false); setFamilyShareOpen(false); setAutoPitchResult(null); }} className="viz-new-btn" data-testid="viz-new">Visualize Another Room</button>
          </div>

          {/* Family Share Panel */}
          {familyShareOpen && (
            <div data-testid="viz-family-share-panel" style={{ background: "linear-gradient(135deg, rgba(206,147,216,0.10), rgba(245,230,240,0.05))", border: "1px solid rgba(206,147,216,0.30)", borderRadius: 12, padding: 18, marginTop: 14 }}>
              <h4 style={{ color: "#CE93D8", margin: "0 0 12px", fontSize: 15 }}>💜 Share This With Family</h4>
              <p style={{ color: "#ADBED1", fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>Send the before/after to a family member with a warm caption. Perfect for showing mom what you built for the kids before any B2B pitch goes out — emotional content first, business content second.</p>
              {familyShare.sent ? (
                <div data-testid="viz-family-share-sent" style={{ padding: 16, borderRadius: 8, background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.3)", color: "#00DC78", fontWeight: 700 }}>
                  ✓ Sent to {familyShare.recipient_email}!
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <input type="text" placeholder="Recipient name (e.g. Charlie's Mom)" value={familyShare.recipient_name} onChange={(e) => setFamilyShare(p => ({ ...p, recipient_name: e.target.value }))} data-testid="family-share-name" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                    <input type="email" placeholder="Recipient email" value={familyShare.recipient_email} onChange={(e) => setFamilyShare(p => ({ ...p, recipient_email: e.target.value }))} data-testid="family-share-email" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <input type="text" placeholder="Room label (e.g. Cheyenne's bedroom)" value={familyShare.room_label} onChange={(e) => setFamilyShare(p => ({ ...p, room_label: e.target.value }))} data-testid="family-share-room" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                    <select value={familyShare.relationship} onChange={(e) => setFamilyShare(p => ({ ...p, relationship: e.target.value }))} data-testid="family-share-relationship" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }}>
                      <option value="Grandma" style={{ background: "#030B1A" }}>Grandma</option>
                      <option value="Auntie" style={{ background: "#030B1A" }}>Auntie</option>
                      <option value="Mom" style={{ background: "#030B1A" }}>Mom</option>
                      <option value="Sister" style={{ background: "#030B1A" }}>Sister</option>
                      <option value="Friend" style={{ background: "#030B1A" }}>Friend</option>
                      <option value="Family" style={{ background: "#030B1A" }}>Family</option>
                    </select>
                  </div>
                  <textarea placeholder="Optional personal note (e.g. 'Used the new Fairy Garden palette — she's gonna lose her mind 💜')" value={familyShare.note} onChange={(e) => setFamilyShare(p => ({ ...p, note: e.target.value }))} rows={2} data-testid="family-share-note" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, marginBottom: 10, fontFamily: "inherit", resize: "vertical" }} />
                  {familyShare.error && <div style={{ color: "#FF5252", fontSize: 12, marginBottom: 10 }}>{familyShare.error}</div>}
                  <button onClick={sendFamilyShare} disabled={familyShare.sending || !familyShare.recipient_email} data-testid="family-share-send" style={{ width: "100%", padding: "11px 14px", borderRadius: 8, border: "1px solid #CE93D8", background: "rgba(206,147,216,0.20)", color: "#CE93D8", fontSize: 13, fontWeight: 800, cursor: familyShare.sending ? "wait" : "pointer" }}>
                    {familyShare.sending ? "Sending…" : `Send to ${familyShare.recipient_email || "family"}`}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Auto-Pitch Result Panel */}
          {autoPitchResult && (
            <div data-testid="viz-autopitch-panel" style={{ background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 12, padding: 18, marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h4 style={{ color: "#00FFFF", margin: 0, fontSize: 15 }}>🎯 B2B Pitch (Studio → Contract Connect-the-Dots)</h4>
                <select value={autoPitchTarget} onChange={(e) => { setAutoPitchTarget(e.target.value); generateAutoPitch(); }} data-testid="viz-autopitch-target" style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(0,255,255,0.3)", background: "rgba(0,255,255,0.06)", color: "#00FFFF", fontSize: 12 }}>
                  <option value="institutional" style={{ background: "#030B1A" }}>Institutional (REITs/AssetMgr)</option>
                  <option value="coalition" style={{ background: "#030B1A" }}>Coalition (DV/Women's)</option>
                  <option value="family_landlord" style={{ background: "#030B1A" }}>Family Landlord</option>
                  <option value="court_receiver" style={{ background: "#030B1A" }}>Court Receiver</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Pitch Paragraph</div>
                <p style={{ color: "#fff", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{autoPitchResult.pitch_paragraph}</p>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Contract Clause (auto-add to unit scope)</div>
                <p style={{ color: "#ADBED1", fontSize: 13, lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>"{autoPitchResult.contract_clause}"</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Value Prop One-Liner</div>
                  <p style={{ color: "#fff", fontSize: 13, lineHeight: 1.5, margin: 0 }}>{autoPitchResult.value_prop_one_liner}</p>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Objection Handler</div>
                  <p style={{ color: "#ADBED1", fontSize: 13, lineHeight: 1.5, margin: 0 }}>{autoPitchResult.objection_handler}</p>
                </div>
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(autoPitchResult.pitch_paragraph)}
                data-testid="viz-autopitch-copy"
                style={{ marginTop: 12, padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(0,255,255,0.4)", background: "transparent", color: "#00FFFF", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Copy Pitch Paragraph
              </button>
            </div>
          )}

          {showSourcing && (
            <div className="viz-sourcing" data-testid="viz-sourcing">
              <h4>Source These Materials</h4>
              <p className="viz-sourcing-sub">Each material is searchable across multiple DFW-relevant vendors. ⭐ marks the recommended vendor for that category. Always sign in with your Pro account for wholesale pricing. Buy tax-free with your Texas Resale Certificate (Form 01-339).</p>
              {complianceSafe && (
                <div data-testid="viz-compliance-banner" style={{
                  background: "linear-gradient(135deg, rgba(0,220,120,0.10), rgba(0,220,120,0.04))",
                  border: "1px solid rgba(0,220,120,0.30)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 14,
                  color: "#fff",
                  fontSize: 13,
                  lineHeight: 1.5
                }}>
                  <strong style={{ color: "#00DC78", display: "block", marginBottom: 4 }}>
                    <i className="fas fa-shield-halved" style={{ marginRight: 6 }}></i>
                    Compliance-Safe Mode ACTIVE — protected on all four sides
                  </strong>
                  Vendor searches include certification keywords (low-VOC, ASTM E84 fire-rated, Microban, FDA food-safe, UL-listed, HUD HQS). Verify each product's data sheet at checkout — these searches PRE-FILTER but don't replace inspection of the actual SKU's compliance documentation.
                </div>
              )}
              {buildVendorList(selectedPalette, scope).map((item, i) => (
                <div key={i} className="viz-sourcing-item" data-testid={`viz-sourcing-item-${i}`}>
                  <div className="viz-sourcing-item-head">
                    <span className="viz-sourcing-cat">{item.category}</span>
                    <strong>{item.label}</strong>
                  </div>
                  {item.compliance && item.compliance.length > 0 && (
                    <div data-testid={`viz-compliance-tags-${i}`} style={{ display: "flex", flexWrap: "wrap", gap: 5, margin: "6px 0 8px" }}>
                      {item.compliance.map((tag, ti) => (
                        <span key={ti} style={{
                          fontSize: 9,
                          padding: "2px 6px",
                          borderRadius: 4,
                          background: "rgba(0,220,120,0.15)",
                          border: "1px solid rgba(0,220,120,0.35)",
                          color: "#00DC78",
                          fontWeight: 700,
                          letterSpacing: "0.04em"
                        }}>
                          <i className="fas fa-shield-halved" style={{ marginRight: 3, fontSize: 8 }}></i>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.autoAdd && item.autoAdd.length > 0 && (
                    <div data-testid={`viz-auto-add-${i}`} style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "0 0 8px" }}>
                      {item.autoAdd.map((reminder, ai) => (
                        <span key={ai} style={{
                          fontSize: 10,
                          padding: "3px 7px",
                          borderRadius: 4,
                          background: "rgba(255,179,71,0.12)",
                          border: "1px solid rgba(255,179,71,0.35)",
                          color: "#FFB347",
                          fontWeight: 700
                        }}>
                          <i className="fas fa-plus" style={{ marginRight: 4, fontSize: 8 }}></i>
                          AUTO-ADD: {reminder}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.prep && (
                    <div data-testid={`viz-prep-${i}`} style={{ margin: "0 0 8px", padding: "8px 10px", borderRadius: 6, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.30)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <i className="fas fa-screwdriver-wrench" style={{ color: "#A78BFA", fontSize: 10 }}></i>
                        <span style={{ fontSize: 10, color: "#A78BFA", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>Prep Required (Crew SOP)</span>
                        <span style={{ marginLeft: "auto", fontSize: 10, color: "#7E92A8" }}>Scrap factor: {Math.round(item.prep.scrap * 100)}%</span>
                      </div>
                      <div style={{ color: "#fff", fontSize: 12, marginBottom: 4 }}>{item.prep.prep}</div>
                      <div style={{ display: "flex", gap: 5, alignItems: "flex-start" }}>
                        <i className="fas fa-triangle-exclamation" style={{ color: "#FFB347", fontSize: 9, marginTop: 3 }}></i>
                        <span style={{ color: "#ADBED1", fontSize: 11, fontStyle: "italic", lineHeight: 1.4 }}>Invisible failure if skipped: {item.prep.invisible}</span>
                      </div>
                    </div>
                  )}
                  <div className="viz-sourcing-vendors">
                    {item.vendors.map((v, vi) => (
                      <a key={vi} href={v.url} target="_blank" rel="noopener noreferrer" className={`viz-vendor-link ${vi === 0 ? "is-recommended" : ""}`} data-testid={`viz-vendor-${i}-${vi}`}>
                        {vi === 0 && "⭐ "}{v.name} ↗
                      </a>
                    ))}
                  </div>
                </div>
              ))}
              {buildVendorList(selectedPalette, scope).length > 0 && (
                <div data-testid="viz-materials-calculator" style={{ marginTop: 18, background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 10, overflow: "hidden" }}>
                  <button
                    onClick={() => setCalcOpen(!calcOpen)}
                    data-testid="viz-calc-toggle"
                    style={{ width: "100%", textAlign: "left", padding: "12px 14px", background: "transparent", border: "none", color: "#00FFFF", fontSize: 13, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <i className="fas fa-calculator"></i>
                    Materials Quantity Calculator (Formulas A &amp; B)
                    <span style={{ marginLeft: "auto", color: "#7E92A8", fontSize: 11, fontWeight: 600 }}>{calcOpen ? "Hide ▴" : "Show ▾"}</span>
                  </button>
                  {calcOpen && (
                    <div style={{ padding: "0 14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ fontSize: 11, color: "#00FFFF", letterSpacing: "0.06em", fontWeight: 800, marginBottom: 8 }}>FORMULA A — ROLLS / SHEETS NEEDED</div>
                        <div style={{ color: "#ADBED1", fontSize: 11, fontFamily: "monospace", marginBottom: 10, lineHeight: 1.4 }}>Yn = (Apanels × (1 + Sf)) / (Wwidth × Lroll) — round up</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                          <label style={{ fontSize: 10, color: "#7E92A8" }}>Total sq ft (A_panels)
                            <input type="number" value={calcArea} onChange={(e) => setCalcArea(e.target.value)} placeholder="e.g. 120" data-testid="viz-calc-area" style={{ marginTop: 3, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                          </label>
                          <label style={{ fontSize: 10, color: "#7E92A8" }}>Scrap factor (Sf)
                            <select value={calcScrap} onChange={(e) => setCalcScrap(e.target.value)} data-testid="viz-calc-scrap" style={{ marginTop: 3, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }}>
                              <option value="0.05" style={{ background: "#030B1A" }}>0.05 — slats / low waste</option>
                              <option value="0.10" style={{ background: "#030B1A" }}>0.10 — clean rectangles</option>
                              <option value="0.15" style={{ background: "#030B1A" }}>0.15 — kitchens w/ cutouts</option>
                              <option value="0.20" style={{ background: "#030B1A" }}>0.20 — bathrooms w/ fixtures</option>
                            </select>
                          </label>
                          <label style={{ fontSize: 10, color: "#7E92A8" }}>Roll width (ft)
                            <input type="number" value={calcRollWidth} onChange={(e) => setCalcRollWidth(e.target.value)} data-testid="viz-calc-rollwidth" style={{ marginTop: 3, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                          </label>
                          <label style={{ fontSize: 10, color: "#7E92A8" }}>Roll length (ft)
                            <input type="number" value={calcRollLength} onChange={(e) => setCalcRollLength(e.target.value)} data-testid="viz-calc-rolllength" style={{ marginTop: 3, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                          </label>
                        </div>
                        {calcRollResult !== null && (
                          <div data-testid="viz-calc-result" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6, background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.30)", color: "#00DC78", fontSize: 14, fontWeight: 700 }}>
                            Order <strong style={{ fontSize: 18 }}>{Math.ceil(calcRollResult)}</strong> rolls/sheets (raw: {calcRollResult.toFixed(2)})
                          </div>
                        )}
                      </div>

                      <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ fontSize: 11, color: "#FFB347", letterSpacing: "0.06em", fontWeight: 800, marginBottom: 8 }}>FORMULA B — CABINET WRAP-AROUND WIDTH</div>
                        <div style={{ color: "#ADBED1", fontSize: 11, fontFamily: "monospace", marginBottom: 10, lineHeight: 1.4 }}>VinylWidth = DoorWidth + (2 × Cw); Cw = 2" (Primer 94 grip)</div>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                          <label style={{ fontSize: 10, color: "#7E92A8", flex: 1, minWidth: 200 }}>Cabinet door width (inches)
                            <input type="number" value={wrapDoorWidth} onChange={(e) => setWrapDoorWidth(e.target.value)} placeholder="e.g. 14.5" data-testid="viz-wrap-door-width" style={{ marginTop: 3, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                          </label>
                          {wrapVinylWidth !== null && (
                            <div data-testid="viz-wrap-result" style={{ padding: "10px 14px", borderRadius: 6, background: "rgba(255,179,71,0.10)", border: "1px solid rgba(255,179,71,0.30)", color: "#FFB347", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                              Cut vinyl <strong style={{ fontSize: 18 }}>{wrapVinylWidth}"</strong> wide
                            </div>
                          )}
                        </div>
                        <div style={{ marginTop: 8, color: "#7E92A8", fontSize: 11, fontStyle: "italic" }}>Vinyl wraps 2" around the back edge so Primer 94 has surface area. Skip this = peel in 30 days.</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="viz-disclaimer">AI-generated visualization for presentation purposes. Actual results may vary based on room conditions, lighting, and material availability.</p>
        </div>
      )}
    </div>
  );
};

export default RoomVisualizer;
